import React, { useState, useEffect, useRef } from "react";
import { useModal, useModalInstance } from "react-modal-state";
import { Modal } from "react-bootstrap";
import Icon from "../common/icons";
import anna from "../../assets/Anna_1.png";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { STRANDS } from "../chatbot/riasec/riasecLogic";
import RegisterInbox from "./register/RegisterInbox";
import { NAME_HINT, isValidFullName } from "./register/registerRules";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/RegisterModal.css";

const RegisterModal = () => {
  const [loading, setLoading] = useState(false); // Manage loading state
  const { isOpen, close } = useModalInstance();
  const { open: openLoginModal } = useModal("login");
  // After sign-up: { email, emailSent } for the "Check your inbox" panel (F18)
  const [done, setDone] = useState(null);
  // The server said this email already has an account (the live check can miss it)
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  // "Wrong email?" brought them back to fix it
  const [fixingEmail, setFixingEmail] = useState(false);
  const emailInput = useRef(null);

  // Each time the dialog opens, start from the form
  useEffect(() => {
    if (!isOpen) return;
    setDone(null);
    setAlreadyRegistered(false);
    setFixingEmail(false);
  }, [isOpen]);

  // Sign in instead, with the email carried over; mode "code" opens "Sign in with a code"
  const goSignIn = (mode) => {
    const email = (done?.email || formData.email || "").trim();
    close();
    setTimeout(() => openLoginModal({ email, ...(mode === "code" && { mode: "code" }) }), 300);
  };

  const wrongEmail = () => {
    setDone(null);
    setFixingEmail(true);
    setEmailAvailability(null);
    setTimeout(() => emailInput.current?.focus(), 50);
  };
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    strand: "",
    sex: "",
    walkIn: "yes", // Default to 'yes' since only Walk-ins can register
  });

  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [birthdayError, setBirthdayError] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [emailSuggestion, setEmailSuggestion] = useState(null); // Add email suggestion state
  const [emailAvailability, setEmailAvailability] = useState(null); // null, 'checking', 'available', 'taken'
  const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);
  
  // Function to check email availability
  const checkEmailAvailability = async (email) => {
    if (!email || !validateEmailFormat(email).isValid) {
      setEmailAvailability(null);
      return;
    }
    
    try {
      console.log('🔍 Checking email availability:', email);
      setEmailAvailability('checking');
      
      const response = await Promise.race([
        api.post('/users/check-duplicate/', {
          email: email.trim()
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]);
      
      // If we get here, no duplicates were found
      console.log('✅ Email is available:', email);
      setEmailAvailability('available');
      
    } catch (error) {
      if (error.message === 'Request timeout') {
        console.log('⏰ Email check timed out:', email);
        setEmailAvailability(null); // Don't show status on timeout
      } else if (error.response?.status === 400 && error.response?.data?.errors?.email) {
        console.log('❌ Email already taken:', email);
        setEmailAvailability('taken');
      } else if (error.response?.status === 401) {
        console.log('🔐 Authentication issue during email check, clearing tokens');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        setEmailAvailability(null);
      } else {
        console.log('⚠️ Error checking email:', error);
        setEmailAvailability(null); // Unknown error, don't show status
      }
    }
  };
  
  const [isWalkIn, setIsWalkIn] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // Track current page (1 or 2)

  // No section list here: sign-ups from this form are always walk-ins, and the class list
  // is for guidance office staff only (F6).

  const [schoolFetchAttempts, setSchoolFetchAttempts] = useState(0);
  const [schoolFetchError, setSchoolFetchError] = useState(null);

  useEffect(() => {
    const fetchSchoolByHostname = async (retryCount = 0) => {
      const hostname = window.location.hostname;
      const maxRetries = 3;
      const retryDelay = 1000 * (retryCount + 1); // Exponential backoff: 1s, 2s, 3s
      
      try {
        console.log(`🏫 Fetching school for hostname: ${hostname} (attempt ${retryCount + 1}/${maxRetries + 1})`);
        
        // Clear any auth tokens if this is a retry after failed login
        if (retryCount > 0) {
          console.log('🔄 Clearing stale auth tokens before retry');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
        }
        
        const response = await Promise.race([
          api.get(`/schools/${hostname}/`),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), 10000)
          )
        ]);
        
        if (response.data?.id) {
          console.log('✅ School fetched successfully:', response.data.id);
          setFormData((prevData) => ({
            ...prevData,
            school_id: response.data.id,
          }));
          setSchoolFetchError(null);
          setSchoolFetchAttempts(0);
        } else {
          throw new Error('School data not found in response');
        }
      } catch (error) {
        console.error(`❌ Error fetching school (attempt ${retryCount + 1}):`, error);
        setSchoolFetchAttempts(retryCount + 1);
        
        if (retryCount < maxRetries) {
          console.log(`⏳ Retrying in ${retryDelay}ms...`);
          setTimeout(() => {
            fetchSchoolByHostname(retryCount + 1);
          }, retryDelay);
        } else {
          const errorMsg = error.message === 'Request timeout' 
            ? 'Request timed out. Please check your connection and try refreshing the page.'
            : error.response?.status === 401 
            ? 'Authentication expired. Please refresh the page and try again.'
            : error.response?.status === 404
            ? 'School not found for this domain. Please contact support.'
            : 'Failed to fetch school information. Please refresh the page and try again.';
          
          setSchoolFetchError(errorMsg);
          toast.error(errorMsg, {
            autoClose: 8000,
            closeOnClick: true,
            pauseOnHover: true
          });
        }
      }
    };

    fetchSchoolByHostname();
  }, []);

  // handleChange: update form field values
  const handleChange = (e) => {
    const { id, value } = e.target;
    const newValue =
      id === "email" ? value.replace(/\s+/g, "").toLowerCase() : value;

    setFormData((prev) => ({
      ...prev,
      [id]: newValue,
    }));

    // Clear full name error when user starts typing
    if (id === "full_name" && fullNameError) {
      setFullNameError("");
    }

    // Real-time email validation with suggestions and availability check
    if (id === "email") {
      setAlreadyRegistered(false);
      console.log('📧 Email field changed, validating:', newValue);
      const emailValidation = validateEmailFormat(newValue);
      console.log('📊 Validation result:', emailValidation);
      
      // Handle email suggestions
      if (!emailValidation.isValid && emailValidation.suggestion) {
        console.log('💡 Setting email suggestion:', emailValidation.suggestion);
        setEmailSuggestion(emailValidation.suggestion);
      } else {
        console.log('❌ Clearing email suggestion');
        setEmailSuggestion(null);
      }
      
      // Handle email availability check with debouncing
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
      
      if (newValue && emailValidation.isValid) {
        // Debounce the availability check by 1 second
        const timeoutId = setTimeout(() => {
          checkEmailAvailability(newValue);
        }, 1000);
        setEmailCheckTimeout(timeoutId);
      } else {
        setEmailAvailability(null);
      }
    }
  };

  // handleWalkInToggle: reset extra fields when toggling walk-in
  const handleWalkInToggle = (e) => {
    const value = e.target.value; // 'yes' or 'no'

    setFormData((prev) => ({
      ...prev,
      walkIn: value,
      ...(value === "yes" && {
        id_no: "",
        mobile_no: "",
        grade_level: "",
        section_id: "",
        birthday: "",
      }),
    }));
  };

  const handleNext = () => {
    if (!formData.full_name || !formData.email) {
      toast.error("Please fill in all required fields before proceeding.");
      return;
    }

    if (!isValidFullName(formData.full_name)) {
      toast.error(NAME_HINT);
      return;
    }

    if (!isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!isWalkIn) {
      setCurrentPage(2);
    }
  };

  const handleBack = () => setCurrentPage(1);
  // Helper function to calculate Levenshtein distance
  const getLevenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  // Helper function to calculate substring similarity for email domain typos
  const getSubstringSimilarity = (domain, targetDomain, targetBase) => {
    const domainLower = domain.toLowerCase();
    const targetLower = targetDomain.toLowerCase();
    const baseLower = targetBase.toLowerCase();
    
    // Check if domain contains the base (like "gmail" in "gdmial")
    let baseMatches = 0;
    let baseSearchIndex = 0;
    
    for (let char of baseLower) {
      const foundIndex = domainLower.indexOf(char, baseSearchIndex);
      if (foundIndex !== -1) {
        baseMatches++;
        baseSearchIndex = foundIndex + 1;
      }
    }
    
    const baseScore = baseMatches / baseLower.length;
    
    // Check overall character overlap
    const domainChars = new Set(domainLower.split(''));
    const targetChars = new Set(targetLower.split(''));
    const intersection = new Set([...domainChars].filter(x => targetChars.has(x)));
    const union = new Set([...domainChars, ...targetChars]);
    const overlapScore = intersection.size / union.size;
    
    // Weight base matching higher than general overlap
    return (baseScore * 0.7) + (overlapScore * 0.3);
  };
  
  // Helper function to calculate character frequency similarity
  const getCharacterFrequencySimilarity = (domain1, domain2) => {
    const getCharFreq = (str) => {
      const freq = {};
      for (let char of str.toLowerCase()) {
        freq[char] = (freq[char] || 0) + 1;
      }
      return freq;
    };
    
    const freq1 = getCharFreq(domain1);
    const freq2 = getCharFreq(domain2);
    
    const allChars = new Set([...Object.keys(freq1), ...Object.keys(freq2)]);
    let totalDifference = 0;
    let maxPossibleDifference = 0;
    
    for (let char of allChars) {
      const count1 = freq1[char] || 0;
      const count2 = freq2[char] || 0;
      totalDifference += Math.abs(count1 - count2);
      maxPossibleDifference += Math.max(count1, count2);
    }
    
    if (maxPossibleDifference === 0) return 1; // Both strings are empty
    
    return 1 - (totalDifference / maxPossibleDifference);
  };

  // Enhanced email validation function with typo detection and suggestions
  const validateEmailFormat = (email) => {
    console.log('🔍 validateEmailFormat called with:', email);
    
    if (!email) {
      console.log('✅ Empty email, returning valid');
      return { isValid: true, message: '', suggestion: null };
    }
    
    // Check email length first
    if (email.length > 254) {
      console.log('❌ Email too long:', email.length);
      return { isValid: false, message: 'Email address is too long', suggestion: null };
    }
    
    // Basic format check - more permissive to allow typo detection
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{1,}$/;
    if (!emailRegex.test(email)) {
      console.log('❌ Email failed basic regex test:', email);
      return { isValid: false, message: 'Invalid email format', suggestion: null };
    }
    
    // Extract domain for typo checking
    const emailParts = email.toLowerCase().split('@');
    if (emailParts.length !== 2) {
      console.log('❌ Email has wrong number of @ symbols:', emailParts.length);
      return { isValid: false, message: 'Invalid email format', suggestion: null };
    }
    
    const [localPart, domain] = emailParts;
    console.log('📧 Processing email parts:', { localPart, domain });
    
    // Common email domain typos and their corrections
    const commonDomains = {
      // Gmail variants
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gmails.com': 'gmail.com',
      'gmaill.com': 'gmail.com',
      'gmail.co': 'gmail.com',
      'gmal.com': 'gmail.com',
      'gail.com': 'gmail.com',
      'gmeil.com': 'gmail.com',
      'gmiail.com': 'gmail.com',
      'gmail.cm': 'gmail.com',
      'gmail.con': 'gmail.com',
      'gmaiil.com': 'gmail.com',
      'gamil.com': 'gmail.com',
      'gmaile.com': 'gmail.com',
      'gmall.com': 'gmail.com',
      
      // Yahoo variants
      'yaho.com': 'yahoo.com',
      'yahooo.com': 'yahoo.com',
      'yahoo.co': 'yahoo.com',
      'ymail.co': 'ymail.com',
      'yahoo.cm': 'yahoo.com',
      'yahoo.con': 'yahoo.com',
      'yahho.com': 'yahoo.com',
      'yhaoo.com': 'yahoo.com',
      'yaoo.com': 'yahoo.com',
      'yahooo.com': 'yahoo.com',
      
      // Outlook/Hotmail variants
      'hotmial.com': 'hotmail.com',
      'hotmai.com': 'hotmail.com',
      'hotmail.co': 'hotmail.com',
      'outlok.com': 'outlook.com',
      'outlook.co': 'outlook.com',
      'live.co': 'live.com',
      'hotmeil.com': 'hotmail.com',
      'hotmall.com': 'hotmail.com',
      'hotmaill.com': 'hotmail.com',
      'outlook.cm': 'outlook.com',
      'outlook.con': 'outlook.com',
      'outlookk.com': 'outlook.com',
      'outllok.com': 'outlook.com',
      'outook.com': 'outlook.com',
      
      // Other common providers
      'aol.co': 'aol.com',
      'icloud.co': 'icloud.com',
      'me.co': 'me.com',
      'aoll.com': 'aol.com',
      'aol.cm': 'aol.com',
      'iclod.com': 'icloud.com',
      'iclould.com': 'icloud.com',
      'icloude.com': 'icloud.com',
      'icloud.cm': 'icloud.com'
    };
    
    // Check for exact domain typo match
    console.log('🎯 Checking exact domain match for:', domain);
    if (commonDomains[domain]) {
      console.log('✅ Found exact match:', domain, '->', commonDomains[domain]);
      const suggestedEmail = `${localPart}@${commonDomains[domain]}`;
      
      // Safety check - don't suggest if it's the same email
      if (suggestedEmail.toLowerCase() === email.toLowerCase()) {
        console.log('⚠️  Suggested email same as original, returning valid');
        return { isValid: true, message: '', suggestion: null };
      }
      
      console.log('💡 Returning suggestion:', suggestedEmail);
      return {
        isValid: false,
        message: 'Possible typo in email domain',
        suggestion: suggestedEmail
      };
    }
    console.log('❌ No exact match found for domain:', domain);
    
    // Check if the domain is already a known valid domain - don't suggest if it's correct
    const popularDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'live.com', 'aol.com', 'icloud.com', 'me.com', 'ymail.com'
    ];
    
    // Extended list of valid domains (including less common but legitimate ones)
    const validDomains = [
      ...popularDomains,
      'protonmail.com', 'tutanota.com', 'fastmail.com', 'zoho.com',
      'mail.com', 'gmx.com', 'inbox.com', 'mail.ru', 'yandex.com',
      'qq.com', '163.com', '126.com', 'sina.com', 'sohu.com'
    ];
    
    // If the domain is already valid, don't suggest anything
    console.log('🔍 Checking if domain is in validDomains:', domain);
    if (validDomains.includes(domain)) {
      console.log('✅ Domain is already valid, no suggestion needed:', domain);
      return { isValid: true, message: '', suggestion: null };
    }
    console.log('⏭️  Domain not in valid list, continuing to advanced detection...');
    
    // Also check if it's a valid TLD pattern (basic check for edu, org, gov, etc.)
    // But only for institutional domains that are unlikely to be typos
    const domainParts = domain.split('.');
    console.log('🏫 Checking TLD pattern for institutional domains:', { domainParts, domain });
    
    if (domainParts.length >= 2) {
      const tld = domainParts[domainParts.length - 1].toLowerCase();
      const domainBase = domainParts[0].toLowerCase();
      
      // Only consider institutional/organizational TLDs that are unlikely to be typos of popular domains
      const institutionalTLDs = ['edu', 'gov', 'mil', 'org'];
      const countryTLDs = ['uk', 'ca', 'au', 'de', 'fr', 'it', 'es', 'nl', 'br', 'ru', 'in', 'cn', 'jp', 'ph', 'sg'];
      
      console.log('🏫 TLD analysis:', { tld, domainBase, isInstitutional: institutionalTLDs.includes(tld), isCountry: countryTLDs.includes(tld) });
      
      // Only skip typo detection for institutional domains or well-known country domains with reasonable base names
      if ((institutionalTLDs.includes(tld) || countryTLDs.includes(tld)) && domainBase.length >= 3) {
        // Additional check: make sure the domain base doesn't look like a typo of popular services
        const popularBases = ['gmail', 'yahoo', 'hotmail', 'outlook', 'live', 'aol', 'icloud'];
        const isLikelyTypoOfPopular = popularBases.some(base => {
          const distance = getLevenshteinDistance(domainBase, base);
          return distance <= 2; // If domain base is very similar to popular service, continue with typo detection
        });
        
        if (!isLikelyTypoOfPopular) {
          console.log('✅ Institutional/country domain appears legitimate, skipping typo detection');
          return { isValid: true, message: '', suggestion: null };
        } else {
          console.log('⚠️  Domain base looks like typo of popular service, continuing with detection...');
        }
      }
      
      // For .com, .net domains, always continue with typo detection unless it's a known valid domain
      console.log('⏭️  Not an institutional domain or possible typo, continuing with advanced detection...');
    }
    
    // Advanced typo detection using multiple strategies
    let bestMatch = null;
    let bestDistance = Infinity;
    let bestSimilarity = 0;
    
    // Strategy 1: Levenshtein distance with more flexible thresholds
    console.log('🔤 Starting Strategy 1: Levenshtein distance analysis');
    for (const popularDomain of popularDomains) {
      const distance = getLevenshteinDistance(domain, popularDomain);
      const maxAllowedDistance = Math.max(2, Math.floor(popularDomain.length * 0.4)); // Allow up to 40% of domain length
      
      console.log(`  📏 ${domain} vs ${popularDomain}: distance=${distance}, maxAllowed=${maxAllowedDistance}`);
      
      if (distance <= maxAllowedDistance && distance < bestDistance) {
        console.log(`  ⭐ New best match found: ${popularDomain} (distance: ${distance})`);
        bestDistance = distance;
        bestMatch = popularDomain;
      }
    }
    console.log('📊 Strategy 1 results:', { bestMatch, bestDistance });
    
    // Strategy 2: Substring matching for common patterns (like "gmail" in "gdmial")
    console.log('🧩 Starting Strategy 2: Substring similarity analysis');
    if (!bestMatch || bestDistance > 3) {
      for (const popularDomain of popularDomains) {
        const domainBase = popularDomain.split('.')[0]; // e.g., "gmail" from "gmail.com"
        const similarity = getSubstringSimilarity(domain, popularDomain, domainBase);
        
        console.log(`  🔍 ${domain} vs ${popularDomain} (base: ${domainBase}): similarity=${similarity.toFixed(3)}`);
        
        if (similarity > bestSimilarity && similarity >= 0.6) { // 60% similarity threshold
          console.log(`  ⭐ New best similarity match: ${popularDomain} (similarity: ${similarity.toFixed(3)})`);
          bestSimilarity = similarity;
          bestMatch = popularDomain;
        }
      }
    } else {
      console.log('⏭️  Skipping Strategy 2 - already have good match from Strategy 1');
    }
    console.log('📊 Strategy 2 results:', { bestMatch, bestSimilarity });
    
    // Strategy 3: Character frequency analysis for scrambled letters
    console.log('📊 Starting Strategy 3: Character frequency analysis');
    if ((!bestMatch || bestDistance > 3) && bestSimilarity < 0.6) {
      for (const popularDomain of popularDomains) {
        const charFreqSimilarity = getCharacterFrequencySimilarity(domain, popularDomain);
        
        console.log(`  📈 ${domain} vs ${popularDomain}: charFreq=${charFreqSimilarity.toFixed(3)}`);
        
        if (charFreqSimilarity > 0.7 && charFreqSimilarity > bestSimilarity) {
          console.log(`  ⭐ New best char freq match: ${popularDomain} (similarity: ${charFreqSimilarity.toFixed(3)})`);
          bestSimilarity = charFreqSimilarity;
          bestMatch = popularDomain;
        }
      }
    } else {
      console.log('⏭️  Skipping Strategy 3 - already have good match or similarity threshold met');
    }
    console.log('📊 Strategy 3 results:', { bestMatch, bestSimilarity });
    
    // Suggest if we found a good match
    console.log('🎯 Final analysis - bestMatch:', bestMatch, 'bestDistance:', bestDistance, 'bestSimilarity:', bestSimilarity);
    
    if (bestMatch && (bestDistance <= Math.max(3, Math.floor(bestMatch.length * 0.5)) || bestSimilarity >= 0.6)) {
      console.log('✅ Match criteria met! Suggesting:', bestMatch);
      const suggestedEmail = `${localPart}@${bestMatch}`;
      
      // Don't suggest if it's the same email
      if (suggestedEmail.toLowerCase() === email.toLowerCase()) {
        console.log('⚠️  Suggested email same as original, returning valid');
        return { isValid: true, message: '', suggestion: null };
      }
      
      console.log('💡 Returning final suggestion:', suggestedEmail);
      return {
        isValid: false,
        message: 'Possible typo in email domain',
        suggestion: suggestedEmail
      };
    }
    console.log('❌ No suitable match found');
    
    // Check for missing TLD (like @gmail without .com)
    const currentDomainParts = domain.split('.');
    if (currentDomainParts.length === 1 && popularDomains.some(d => d.startsWith(domain + '.'))) {
      const suggestedDomain = popularDomains.find(d => d.startsWith(domain + '.'));
      if (suggestedDomain) {
        const suggestedEmail = `${localPart}@${suggestedDomain}`;
        
        // Safety check - don't suggest if it's the same email
        if (suggestedEmail.toLowerCase() === email.toLowerCase()) {
          return { isValid: true, message: '', suggestion: null };
        }
        
        return {
          isValid: false,
          message: 'Missing domain extension',
          suggestion: suggestedEmail
        };
      }
    }
    
    console.log('✅ No suggestions needed, email appears valid');
    return { isValid: true, message: '', suggestion: null };
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handle accepting email suggestion
  const acceptEmailSuggestion = () => {
    if (emailSuggestion) {
      setFormData((prev) => ({
        ...prev,
        email: emailSuggestion,
      }));
      setEmailSuggestion(null);
    }
  };

  // Full name validation: isValidFullName in register/registerRules.js takes any alphabet (Niña Peñaflor)

  // Password validation function
  const isValidPassword = (password) => {
    // At least one uppercase letter and at least 8 characters long
    const passwordRegex = /^(?=.*[A-Z]).{8,}$/;
    return passwordRegex.test(password);
  };

  // Mobile number validation
  const isValidMobile = (mobile) => {
    const mobileRegex = /^[0-9]{10,15}$/;
    return mobileRegex.test(mobile);
  };
  const handleBirthdayChange = (e) => {
    const newBirthday = e.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      birthday: newBirthday,
    }));

    // Convert the input string to a date object
    const birthDate = new Date(newBirthday);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates accurately

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (birthDate > today) {
      setBirthdayError("Birthday cannot be in the future.");
    } else if (age < 10 || age > 100) {
      setBirthdayError("Valid age range: 10 to 100 years old.");
    } else {
      setBirthdayError("");
    }
  };

  // Birthday validation
  const isValidBirthday = (birthday) => {
    if (!birthday) return false; // ✅ Ensure it's not empty

    const birthDate = new Date(birthday);
    if (isNaN(birthDate.getTime())) return false; // ✅ Handle invalid dates

    const today = new Date();
    today.setHours(0, 0, 0, 0); // ✅ Normalize to avoid time-related errors

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age >= 10 && age <= 100;
  };

  // Real-time validation for password
  const handlePasswordBlur = () => {
    if (!isValidPassword(formData.password)) {
      setPasswordError(
        "Password must include at least one uppercase letter and be at least 8 characters long.",
      );
    } else {
      setPasswordError("");
    }
  };

  // Real-time validation for confirm password
  const handleConfirmPasswordBlur = () => {
    if (formData.password !== formData.confirm_password) {
      setConfirmPasswordError("Passwords do not match.");
    } else {
      setConfirmPasswordError("");
    }
  };

  // Real-time validation for mobile
  const handleMobileBlur = () => {
    if (!isValidMobile(formData.mobile_no)) {
      setMobileError("Please enter a valid mobile number (10-15 digits).");
    } else {
      setMobileError("");
    }
  };

  // Real-time validation for full name
  const handleFullNameBlur = () => {
    if (!isValidFullName(formData.full_name)) {
      setFullNameError(NAME_HINT);
    } else {
      setFullNameError("");
    }
  };
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate full name before submitting
    if (!isValidFullName(formData.full_name)) {
      setFullNameError(NAME_HINT);
      setLoading(false);
      return;
    }
    // A possible typo ("gmial.com") keeps Register off until it's fixed (F18)
    if (!validateEmailFormat(formData.email).isValid) {
      setLoading(false);
      return;
    }

    // Build payload: the strand's stored value (STRANDS), or null when none was picked
    const registerData = {
      ...formData,
      full_name: formData.full_name.trim().replace(/\s+/g, " "),
      strand: formData.strand || null,
    };

    try {
      const response = await api.post("/register/", registerData, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("API Response:", response);

      if (response.status === 201) {
        // A panel that stays (RegisterInbox) instead of a toast that disappears. The form keeps
        // its fields so "Wrong email?" only needs the email fixed.
        setDone({ email: registerData.email, emailSent: response.data?.email_sent !== false });
        setFixingEmail(false);
        setLoading(false);
        return;
      }

      // Handle non-201 errors
      toast.error(
        response.data?.message || "Registration failed. Please try again.",
        {
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          position: "top-right",
        },
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Error:", err);

      let errorMessage = "Registration failed. Please check your details.";
      
      // Handle specific error messages
      if (err.response?.data?.message) {
        const serverMessage = err.response.data.message;
        
        if (serverMessage.includes("Section has no department") || 
            serverMessage.includes("department")) {
          errorMessage = "Registration system configuration issue. Please contact support or try again later.";
        } else if (serverMessage.includes("Email already registered")) {
          // Not a toast: the email field offers Sign in / Get a code instead
          setAlreadyRegistered(true);
          return;
        } else if (serverMessage.includes("School not found")) {
          errorMessage = "School information not found. Please refresh the page and try again.";
        } else {
          errorMessage = serverMessage;
        }
      }
      
      toast.error(errorMessage, {
        autoClose: 7000, // Longer duration for error messages
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        position: "top-right",
      });
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={close}
      size="default"
      dialogClassName="am-auth-dialog"
      centered
      aria-labelledby="register-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="register-title">Create your account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {done ? (
          <RegisterInbox
            email={done.email}
            emailSent={done.emailSent}
            onCode={() => goSignIn("code")}
            onWrongEmail={wrongEmail}
          />
        ) : (
        <form className="am-form">
          <div className="am-intro">
            <img src={anna} alt="Anna" className="am-avatar" />
            <h3 className="am-intro-title">Hi! Let’s get you started</h3>
            <p className="am-intro-text">
              After you register, we’ll email you a link to set your password.
            </p>
          </div>

          {fixingEmail && (
            <div className="am-note rg-fix" role="status">
              <Icon name="mail" size={18} />
              <span>Fix your email below, then tap Register again.</span>
            </div>
          )}

          {/* Show school fetch status */}
          {schoolFetchAttempts > 0 && !formData.school_id && !schoolFetchError && (
            <p className="am-note" role="status">
              <span className="spinner-border" aria-hidden="true" />
              <span>Connecting to school system... (attempt {schoolFetchAttempts}/4)</span>
            </p>
          )}

          {schoolFetchError && (
            <div className="am-note is-danger" role="alert">
              <Icon name="alert-triangle" size={18} />
              <span>
                {schoolFetchError}{" "}
                <button
                  type="button"
                  className="am-link"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </button>
              </span>
            </div>
          )}

          {/* Full Name */}
          <div className="am-field">
            <label className="am-label" htmlFor="full_name">Full Name</label>
            <span className={`am-input${fullNameError ? " has-error" : ""}`}>
              <Icon name="user" size={20} />
              <input
                type="text"
                id="full_name"
                placeholder="First and last name"
                autoComplete="name"
                value={formData.full_name}
                onChange={handleChange}
                onBlur={handleFullNameBlur}
                aria-invalid={!!fullNameError}
                required
              />
            </span>
            {fullNameError && (
              <span className="am-error am-field-msg">
                <Icon name="alert-circle" size={15} />
                {fullNameError}
              </span>
            )}
          </div>

          {/* Email */}
          <div className="am-field">
            <label className="am-label" htmlFor="email">Email</label>
            <span
              className={`am-input${
                (formData.email && !validateEmailFormat(formData.email).isValid) ||
                emailAvailability === 'taken' || alreadyRegistered
                  ? " has-error"
                  : ""
              }`}
            >
              <Icon name="mail" size={20} />
              <input
                ref={emailInput}
                type="email"
                id="email"
                placeholder="you@example.com"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </span>

            {/* Email validation feedback */}
            {formData.email && (() => {
              const emailValidation = validateEmailFormat(formData.email);
              if (!emailValidation.isValid) {
                return (
                  <>
                    <span className="am-error am-field-msg">
                      <Icon name="alert-triangle" size={15} />
                      {emailValidation.message}
                    </span>
                    {emailValidation.suggestion && (
                      <span className="am-hint am-field-msg">
                        <Icon name="lightbulb" size={15} />
                        <span>
                          Did you mean{" "}
                          <button
                            type="button"
                            className="am-link"
                            onClick={acceptEmailSuggestion}
                            title="Click to use this suggestion"
                          >
                            {emailValidation.suggestion}
                          </button>
                          ?
                        </span>
                      </span>
                    )}
                  </>
                );
              }
              return null;
            })()}

            {/* Email availability status - only show problems or checking */}
            {emailAvailability === 'checking' && (
              <span className="am-hint am-field-msg" role="status">
                <span className="spinner-border" aria-hidden="true" />
                Checking availability...
              </span>
            )}
            {(emailAvailability === 'taken' || alreadyRegistered) && (
              <div className="am-note rg-already" role="alert">
                <Icon name="user-check" size={18} />
                <span className="rg-already-text">
                  <b>This email already has an Anna account.</b> Sign in with it, or get a code by email if you
                  haven’t set a password yet.
                </span>
                <span className="rg-already-actions">
                  <button type="button" className="am-btn is-primary is-small" onClick={() => goSignIn("password")}>
                    <Icon name="sign-in" size={16} />
                    Sign in
                  </button>
                  <button type="button" className="am-btn is-soft is-small" onClick={() => goSignIn("code")}>
                    <Icon name="key" size={16} />
                    Get a code
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Strand */}
          <div className="am-field">
            <label className="am-label" htmlFor="strand">
              Strand <span className="am-optional">(optional)</span>
            </label>
            <span className="am-input">
              <Icon name="graduation-cap" size={20} />
              <select
                id="strand"
                value={formData.strand}
                onChange={handleChange}
              >
                <option value="">
                  Select Strand (Optional)
                </option>
                {/* The app's one strand list (riasecLogic STRANDS): value is what the server stores */}
                {STRANDS.map((strand) => (
                  <option key={strand.value} value={strand.value}>{strand.label}</option>
                ))}
              </select>
            </span>
          </div>

          {/* Sex */}
          <div className="am-field">
            <label className="am-label" htmlFor="sex">Sex</label>
            <span className="am-input">
              <Icon name="gender" size={20} />
              <select
                id="sex"
                value={formData.sex}
                onChange={handleChange}
                required
              >
                <option value="" hidden>
                  Select Sex
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </span>
          </div>

          {/* Submit Button */}
          <button
            className="am-btn is-primary is-block is-large"
            type="button"
            onClick={handleRegister}
            disabled={
              loading ||
              !formData.full_name?.trim() ||
              !formData.email?.trim() ||
              !formData.sex?.trim() ||
              // Any email problem keeps it off, a typo suggestion too: one tap on it fixes the email (F18)
              (formData.email && !validateEmailFormat(formData.email).isValid) ||
              emailAvailability === 'taken' ||
              alreadyRegistered ||
              schoolFetchError || // Disable if school fetch failed
              !formData.school_id // Disable if no school ID
            }
          >
            {loading ? (
              <>
                <span
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></span>
                Registering...
              </>
            ) : (
              <>
                <Icon name="user-plus" size={18} />
                Register
              </>
            )}
          </button>
        </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RegisterModal;
